import { useMemo, useState } from "preact/hooks"
import useSWR from "swr"

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import * as ForecastIO from "../api/forecast_io_api"
import type { ForecastVersion } from "../types"
import { type CustomerVariance, summarize_budget_vs_actual } from "../utils/budget_vs_actual"
import { format_period_column_label } from "../utils/periods"

interface BudgetVsActualPanelProps {
  version_id: string
  /** The budget to compare against; the primary plan when null. */
  budget_version_id: string | null
  versions: ForecastVersion[]
  on_budget_version_change: (id: string | null) => void
  className?: string
}

function currency(value: number | null): string {
  if (value == null) return "—"
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value)
}

function signed_currency(value: number): string {
  const text = currency(Math.abs(value))
  return value < 0 ? `−${text}` : value > 0 ? `+${text}` : text
}

function percent(value: number | null): string {
  if (value == null) return "—"
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`
}

function plain_percent(value: number | null): string {
  return value == null ? "—" : `${value.toFixed(1)}%`
}

function variance_class(value: number): string {
  if (value > 0) return "text-emerald-700"
  if (value < 0) return "text-rose-700"
  return "text-gray-700"
}

/**
 * Item 24, the minimal cut: a separate panel (not more grid columns) with budget, actual,
 * variance and — where the catalog prices the SKUs — COGS and gross margin, per customer, for
 * the periods that have actuals.
 */
export function BudgetVsActualPanel(props: BudgetVsActualPanelProps) {
  const { version_id, budget_version_id, versions, on_budget_version_change, className } = props
  const [period_filter, set_period_filter] = useState<string>("all")

  const { data, error, isLoading } = useSWR(
    ["forecast-budget-vs-actual", version_id, budget_version_id],
    () => ForecastIO.get_budget_vs_actual(version_id, budget_version_id)
  )

  const summary = useMemo(
    () => (data ? summarize_budget_vs_actual(data.rows, period_filter === "all" ? undefined : [period_filter]) : null),
    [data, period_filter]
  )
  const has_cogs = summary != null && (summary.total.actual_cogs != null || summary.total.budget_cogs != null)
  const candidates = versions.filter((v: ForecastVersion) => !v.archived_at)

  return (
    <section className={`rounded-lg border border-gray-200 bg-white p-4 ${className ?? ""}`} data-testid="budget-vs-actual-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Budget vs actual</h3>
          <p className="text-xs text-gray-500">
            {data
              ? `Budget: ${data.budget_version_name} · ${data.periods.length} period${data.periods.length === 1 ? "" : "s"} with actuals${
                  data.cogs_coverage_pct != null ? ` · COGS known for ${data.cogs_coverage_pct.toFixed(0)}% of actual cases` : ""
                }`
              : "Per customer, for the periods that have actuals"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={budget_version_id ?? "__primary__"} onValueChange={(value: string) => on_budget_version_change(value === "__primary__" ? null : value)}>
            <SelectTrigger className="h-8 w-56 text-xs">
              <SelectValue placeholder="Budget version" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__primary__">Primary plan</SelectItem>
              {candidates.map((v: ForecastVersion) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={period_filter} onValueChange={set_period_filter}>
            <SelectTrigger className="h-8 w-40 text-xs">
              <SelectValue placeholder="All periods" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All periods</SelectItem>
              {(data?.periods ?? []).map((period: string) => (
                <SelectItem key={period} value={period}>
                  {format_period_column_label(period)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error ? <p className="text-sm text-red-700">{error instanceof Error ? error.message : String(error)}</p> : null}
      {isLoading ? <p className="text-sm text-gray-500">Loading…</p> : null}
      {summary && summary.rows.length === 0 && !isLoading ? (
        <p className="text-sm text-gray-600">No actuals on this version yet. Sync actuals, then come back here.</p>
      ) : null}

      {summary && summary.rows.length > 0 ? (
        <div className="overflow-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-gray-600">
              <tr>
                <th className="px-2 py-1.5 font-medium">Customer</th>
                <th className="px-2 py-1.5 text-right font-medium">Budget</th>
                <th className="px-2 py-1.5 text-right font-medium">Actual</th>
                <th className="px-2 py-1.5 text-right font-medium">Variance</th>
                <th className="px-2 py-1.5 text-right font-medium">Var %</th>
                <th className="px-2 py-1.5 text-right font-medium">Cases Δ</th>
                {has_cogs ? (
                  <>
                    <th className="px-2 py-1.5 text-right font-medium">Actual COGS</th>
                    <th className="px-2 py-1.5 text-right font-medium">Gross margin</th>
                    <th className="px-2 py-1.5 text-right font-medium">GM %</th>
                    <th className="px-2 py-1.5 text-right font-medium">Budget GM %</th>
                  </>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {summary.rows.map((row: CustomerVariance) => (
                <VarianceRow key={row.customer} row={row} has_cogs={has_cogs} />
              ))}
            </tbody>
            <tfoot className="border-t-2 border-gray-300 font-semibold">
              <VarianceRow row={summary.total} has_cogs={has_cogs} />
            </tfoot>
          </table>
        </div>
      ) : null}
    </section>
  )
}

function VarianceRow(props: { row: CustomerVariance; has_cogs: boolean }) {
  const { row, has_cogs } = props
  return (
    <tr className="border-t border-gray-100">
      <td className="px-2 py-1.5">{row.customer}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{currency(row.budget_dollars)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums">{currency(row.actual_dollars)}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums ${variance_class(row.variance_dollars)}`}>{signed_currency(row.variance_dollars)}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums ${variance_class(row.variance_dollars)}`}>{percent(row.variance_pct)}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums ${variance_class(row.variance_cases)}`}>
        {row.variance_cases > 0 ? "+" : ""}
        {Math.round(row.variance_cases).toLocaleString()}
      </td>
      {has_cogs ? (
        <>
          <td className="px-2 py-1.5 text-right tabular-nums">{currency(row.actual_cogs)}</td>
          <td className="px-2 py-1.5 text-right tabular-nums">{currency(row.actual_margin_dollars)}</td>
          <td className="px-2 py-1.5 text-right tabular-nums">{plain_percent(row.actual_margin_pct)}</td>
          <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">{plain_percent(row.budget_margin_pct)}</td>
        </>
      ) : null}
    </tr>
  )
}
